/**
 * Message — AI Elements pattern
 * Role-based message rendering with support for text, tool results, and previews.
 */
import React, { type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { ExternalLink } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";

interface MessageProps {
  from: "user" | "assistant" | "system";
  children: ReactNode;
  className?: string;
}

export const Message: React.FC<MessageProps> = ({
  from,
  children,
  className,
}) => {
  return (
    <div
      className={cn(
        "flex",
        from === "user" ? "justify-end" : "justify-start",
        className,
      )}
    >
      {from === "user" ? (
        <div className="min-w-0 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600 break-words overflow-hidden">
          {children}
        </div>
      ) : (
        <div className="bg-transparent min-w-0 rounded-lg px-3 py-2 text-sm text-[#364153] dark:text-slate-200 border-[1.5px] border-transparent break-words overflow-hidden">
          {children}
        </div>
      )}
    </div>
  );
};

interface MessageContentProps {
  children: ReactNode;
  className?: string;
}

export const MessageContent: React.FC<MessageContentProps> = ({
  children,
  className,
}) => {
  return <div className={cn("min-w-0 space-y-1", className)}>{children}</div>;
};

interface MessageResponseProps {
  children: string;
  className?: string;
}

const renderInline = (text: string): ReactNode[] => {
  const parts: ReactNode[] = [];
  const regex = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    parts.push(
      <strong key={key++} className="font-semibold">
        {match[1]}
      </strong>,
    );
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
};

const renderMarkdown = (text: string): ReactNode => {
  const lines = text.split("\n");
  const elements: ReactNode[] = [];
  let listItems: ReactNode[] = [];
  let key = 0;

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={key++} className="list-disc pl-4 space-y-0.5">
          {listItems}
        </ul>,
      );
      listItems = [];
    }
  };

  for (const line of lines) {
    const headingMatch = line.match(/^(#{1,4})\s+(.+)$/);
    if (headingMatch) {
      flushList();
      const level = headingMatch[1].length;
      const cls =
        level <= 2
          ? "text-sm font-semibold mt-2 mb-0.5"
          : "text-sm font-medium mt-1.5 mb-0.5";
      elements.push(
        <div key={key++} className={cls}>
          {renderInline(headingMatch[2])}
        </div>,
      );
      continue;
    }

    const listMatch = line.match(/^[-*]\s+(.+)$/);
    if (listMatch) {
      listItems.push(<li key={key++}>{renderInline(listMatch[1])}</li>);
      continue;
    }

    flushList();

    if (line.trim() === "") {
      elements.push(<div key={key++} className="h-1" />);
    } else {
      elements.push(
        <span key={key++}>
          {renderInline(line)}
          {"\n"}
        </span>,
      );
    }
  }

  flushList();
  return elements;
};

export const MessageResponse: React.FC<MessageResponseProps> = ({
  children,
  className,
}) => {
  return (
    <div
      className={cn(
        "min-w-0 break-words whitespace-pre-wrap leading-relaxed",
        className,
      )}
    >
      {renderMarkdown(children)}
    </div>
  );
};

interface MessageActionsProps {
  children: ReactNode;
  className?: string;
}

export const MessageActions: React.FC<MessageActionsProps> = ({
  children,
  className,
}) => {
  return (
    <div
      className={cn(
        "mt-1.5 flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100",
        className,
      )}
    >
      {children}
    </div>
  );
};

interface ToolResultProps {
  name: string;
  success: boolean;
  summary: string;
  needsInput?: boolean;
  className?: string;
}

const TOOL_RESULT_SHORT_I18N_KEY: Readonly<Record<string, string>> = {
  search: "assistant.toolResultShortSearch",
  org_graph: "assistant.toolResultShortOrgGraph",
  org_query: "assistant.toolResultShortOrgQuery",
  filter: "assistant.toolResultShortFilter",
  heatmap: "assistant.toolResultShortHeatmap",
  color_by: "assistant.toolResultShortColorBy",
  badge: "assistant.toolResultShortBadge",
  label: "assistant.toolResultShortLabel",
  highlight: "assistant.toolResultShortHighlight",
  clear_rules: "assistant.toolResultShortClearRules",
  quick_edit: "assistant.toolResultShortQuickEdit",
  scenario: "assistant.toolResultShortScenario",
  layout: "assistant.toolResultShortLayout",
  as_of_date: "assistant.toolResultShortAsOfDate",
  shape_style: "assistant.toolResultShortShapeStyle",
  focus_view: "assistant.toolResultShortFocusView",
};

const AGENT_TRACE_STEP_I18N_KEY: Readonly<Record<string, string>> = {
  "Understanding your request": "assistant.traceUnderstandingRequest",
  "Preparing prompt and tools": "assistant.tracePreparingPrompt",
  "Model is thinking": "assistant.traceModelThinking",
  "Finalizing tool actions": "assistant.traceFinalizingToolActions",
  "Applying tool plan": "assistant.traceApplyingToolPlan",
  "Generating primary chip branches": "assistant.traceGeneratingChipBranches",
};

const RUNNING_TOOL_TRACE = /^Running (.+)$/;

/** Localize agent loading trace lines (server + client emit canonical English). */
export function translateAgentLoadingTraceStep(
  step: string,
  t: (key: string, o?: Record<string, unknown>) => string,
): string {
  const mapped = AGENT_TRACE_STEP_I18N_KEY[step];
  if (mapped) {
    return t(mapped, { defaultValue: step });
  }
  const running = RUNNING_TOOL_TRACE.exec(step);
  if (running) {
    const toolLabel = translateToolResultName(running[1], t);
    return t("assistant.traceRunningTool", {
      defaultValue: `Running ${toolLabel}`,
      tool: toolLabel,
    });
  }
  return step;
}

function translateToolResultName(
  name: string,
  t: (key: string, o?: Record<string, unknown>) => string,
): string {
  if (name === "genogram_proposed_changes") {
    return t("assistant.genogramToolProposedChanges", {
      defaultValue: "Proposed changes",
    });
  }
  if (name === "genogram_fieldpack_enrich") {
    return t("assistant.genogramToolFieldpackEnrich", {
      defaultValue: "Field enrichment",
    });
  }
  if (name === "preset") {
    return t("assistant.toolNamePreset", { defaultValue: "Preset" });
  }
  const shortKey = TOOL_RESULT_SHORT_I18N_KEY[name];
  const fallback = toolDisplayName(name);
  if (shortKey) {
    return t(shortKey, { defaultValue: fallback });
  }
  return fallback;
}

export const ToolResult: React.FC<ToolResultProps> = ({
  name,
  success,
  summary,
  needsInput = false,
  className,
}) => {
  const { t } = useTranslation("diagram");
  const shortLabel = success && !needsInput ? translateToolResultName(name, t) : summary;

  return (
    <div
      className={cn(
        "inline-flex items-center gap-1 text-[11px] text-muted-foreground",
        className,
      )}
    >
      <span
        className={cn(
          "shrink-0",
          needsInput ? "text-amber-600" : success ? "text-emerald-600" : "text-rose-500",
        )}
      >
        {needsInput ? "!" : success ? "✓" : "✗"}
      </span>
      <span className="leading-snug">{shortLabel}</span>
    </div>
  );
};

function toolDisplayName(name: string): string {
  const labels: Record<string, string> = {
    process_critical_path: "Critical path",
    process_compliance_check: "Compliance review",
    process_role_analysis: "Role assignments",
    process_management_summary: "Management summary",
    propose_process_changes: "Proposed process changes",
    set_shape_color: "Updated shape colors",
    search: "Searched",
    org_graph: "Checked org data",
    org_query: "Queried org",
    filter: "Applied filter",
    heatmap: "Applied heatmap",
    color_by: "Applied color",
    badge: "Added badge",
    label: "Added label",
    highlight: "Highlighted",
    clear_rules: "Cleared rules",
    quick_edit: "Updated field",
    scenario: "Generated scenario",
    layout: "Applied layout",
    as_of_date: "Set date",
    shape_style: "Changed style",
    focus_view: "Set focus view",
  };
  return labels[name] ?? name;
}

interface PreviewResultProps {
  name: string;
  additions: number;
  removals: number;
  moves: number;
  updates: number;
  isActive: boolean;
  onReject: () => void;
  onModify: () => void;
  onOpenDetailedView?: () => void;
  /**
   * DL-2: when set, renders an explicit "Apply" button that calls the
   * tool's `onConfirm` callback. For scenario-based tools (orgchart
   * `scenario` tool) Apply happens via the Scenarios panel, so the
   * prop stays undefined and we keep the legacy Discard/Modify-only
   * UI. For design-mode tools (`apply_pattern`, `compose_service`),
   * the tool wires its `result.preview.onConfirm` to this so the user
   * can confirm + execute inline in the chat.
   */
  onConfirm?: () => void;
  className?: string;
}

export const PreviewResult: React.FC<PreviewResultProps> = ({
  name,
  additions,
  removals,
  moves,
  updates,
  isActive,
  onConfirm,
  onReject,
  onModify,
  onOpenDetailedView,
  className,
}) => {
  const { t } = useTranslation("diagram");
  return (
    <div
      className={cn("space-y-1.5", className)}
      data-testid="tid.agent.preview"
      data-tool-name={name}
      data-active={isActive ? "true" : "false"}
    >
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <span className="text-blue-500">&#9670;</span>
        <span>
          {t("assistant.previewScenarioLabel", { defaultValue: "Scenario:" })}
          {additions > 0 && (
            <span className="ml-1 text-emerald-600">+{additions}</span>
          )}
          {removals > 0 && (
            <span className="ml-1 text-rose-500">−{removals}</span>
          )}
          {moves > 0 && <span className="ml-1">↔{moves}</span>}
          {updates > 0 && <span className="ml-1">✎{updates}</span>}
        </span>
      </div>
      {isActive ? (
        <div className="flex gap-2 -ml-2 items-center flex-wrap">
          {onConfirm ? (
            <button
              onClick={() => onConfirm()}
              data-testid="tid.agent.preview.apply"
              className="text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 px-2.5 py-1 rounded-md transition-colors"
            >
              {t("assistant.previewApply", { defaultValue: "Apply" })}
            </button>
          ) : null}
          {onOpenDetailedView ? (
            <button
              onClick={() => onOpenDetailedView()}
              className="flex gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              <ExternalLink width={16} height={16} />{" "}
              {t("assistant.previewDetailedView", {
                defaultValue: "Detailed View",
              })}
            </button>
          ) : null}
          <button
            onClick={() => onReject()}
            data-testid="tid.agent.preview.discard"
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            {t("assistant.previewDiscard", { defaultValue: "Discard" })}
          </button>
          <button
            onClick={() => onModify()}
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            {t("assistant.previewModify", { defaultValue: "Modify" })}
          </button>
        </div>
      ) : (
        <div className="text-xs text-muted-foreground">
          {t("assistant.previewScenarioCreated", {
            defaultValue: "Scenario created.",
          })}
        </div>
      )}
    </div>
  );
};
